// frontend/src/pages/AddDevice.js
import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Spinner } from 'react-bootstrap';
import api from '../api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';
import DOMPurify from 'dompurify';

const AddDevice = () => {
    const [formData, setFormData] = useState({
        deviceId: '',
        name: '',
        location: '',
        description: '',
        tcpAddress: '',
        videoStreamAddress: '',
        status: '可用',
        isIdle: true,
        turnCost: '2.8',
        fsFactor: '0.145'
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [partitions, setPartitions] = useState([]);
    const [fetchingPartitions, setFetchingPartitions] = useState(true);
    const [deviceType, setDeviceType] = useState('pinball'); // 'pinball' or 'sanqi'

    const navigate = useNavigate();

    useEffect(() => {
        fetchPartitions();
    }, []);

    const fetchPartitions = async () => {
        try {
            const res = await api.get('/api/partitions');
            setPartitions(res.data);
        } catch (error) {
            console.error('获取分区列表失败:', error);
            toast.error(error.response?.data?.message || '获取分区列表失败');
        } finally {
            setFetchingPartitions(false);
        }
    };

    const ipPortRegex = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}:(6553[0-5]|655[0-2]\d|65[0-4]\d{2}|6[0-4]\d{3}|[1-5]?\d{1,4})$/;

    const validate = () => {
        const newErrors = {};

        if (!formData.deviceId.trim()) newErrors.deviceId = '设备ID是必填项';
        if (!formData.name.trim()) newErrors.name = '设备名称是必填项';
        if (!formData.location) newErrors.location = '设备分区是必填项';
        
        if (!formData.tcpAddress.trim()) {
            newErrors.tcpAddress = 'TCP地址是必填项';
        } else if (!ipPortRegex.test(formData.tcpAddress)) {
            newErrors.tcpAddress = 'TCP地址格式不正确 (IP:端口)';
        }
        if (!formData.videoStreamAddress.trim()) {
            newErrors.videoStreamAddress = '视频流地址是必填项';
        } else if (!validator.isURL(formData.videoStreamAddress, { require_protocol: true, protocols: ['http','https','webrtc'] })) {
            newErrors.videoStreamAddress = '视频流地址URL格式不正确（仅支持 http、https、webrtc）';
        }

        const costNum = parseFloat(formData.turnCost);
        const fsNum   = parseFloat(formData.fsFactor);
        if (Number.isNaN(costNum) || costNum < 0) {
            newErrors.turnCost = '每转分数必须是一个非负数字';
        }
        if (Number.isNaN(fsNum) || fsNum < 0) {
            newErrors.fsFactor = '下分比例必须是一个非负数字';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: undefined
            });
        }
    };

    const handleDeviceTypeChange = (e) => {
        const type = e.target.value;
        setDeviceType(type);
        if (type === 'sanqi') {
            navigate('/sanqi');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            toast.error('请修正表单中的错误');
            return;
        }

        setLoading(true);
        try {
            const sanitizedData = {
                ...formData,
                description: DOMPurify.sanitize(formData.description)
            };
            await api.post('/api/devices', sanitizedData);
            toast.success('设备添加成功');
            navigate('/admin/devices');
        } catch (error) {
            console.error('添加设备失败:', error);
            if (error.response && error.response.data && error.response.data.error) {
                const backendErrors = error.response.data.error;
                const newErrors = {};
                for (const key in backendErrors) {
                    newErrors[key] = backendErrors[key].message;
                }
                setErrors(newErrors);
                toast.error('请修正表单中的错误');
            } else {
                toast.error(error.response?.data?.message || '添加设备失败');
            }
        } finally {
            setLoading(false);
        }
    };

    if (fetchingPartitions) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">加载中...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container>
            <Row className="justify-content-md-center">
                <Col md={8}>
                    <h2 className="mt-4">添加设备</h2>
                    <Form.Group controlId="formDeviceType" className="mt-3">
                        <Form.Label>设备模型</Form.Label>
                        <Form.Select
                            value={deviceType}
                            onChange={handleDeviceTypeChange}
                        >
                            <option value="pinball">钢珠机</option>
                            <option value="sanqi">三七机</option>
                        </Form.Select>
                    </Form.Group>
                    {(deviceType === 'pinball' || deviceType === 'sanqi') && (
                        <Form onSubmit={handleSubmit}>
                            <Form.Group controlId="formDeviceId" className="mt-3">
                                <Form.Label>设备ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="请输入设备ID"
                                    name="deviceId"
                                    value={formData.deviceId}
                                    onChange={handleChange}
                                    isInvalid={!!errors.deviceId}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.deviceId}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="formName" className="mt-3">
                                <Form.Label>设备名称</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="请输入设备名称"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="formLocation" className="mt-3">
                                <Form.Label>设备分区</Form.Label>
                                <Form.Select
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    isInvalid={!!errors.location}
                                    required
                                >
                                    <option value="">选择分区</option>
                                    {partitions.map(partition => (
                                        <option key={partition._id} value={partition._id}>
                                            {partition.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.location}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="formDescription" className="mt-3">
                                <Form.Label>设备简介</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="请输入设备简介"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group controlId="formTcpAddress" className="mt-3">
                                <Form.Label>设备TCP地址</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="例如：192.168.1.10:8080"
                                    name="tcpAddress"
                                    value={formData.tcpAddress}
                                    onChange={handleChange}
                                    isInvalid={!!errors.tcpAddress}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.tcpAddress}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="formVideoStreamAddress" className="mt-3">
                                <Form.Label>设备视频流地址</Form.Label>
                                <Form.Control
                                    type="url"
                                    placeholder="请输入设备视频流地址"
                                    name="videoStreamAddress"
                                    value={formData.videoStreamAddress}
                                    onChange={handleChange}
                                    isInvalid={!!errors.videoStreamAddress}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.videoStreamAddress}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="formStatus" className="mt-3">
                                <Form.Label>状态</Form.Label>
                                <Form.Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="可用">可用</option>
                                    <option value="维修">维修</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group controlId="formIsIdle" className="mt-3">
                                <Form.Check 
                                    type="checkbox"
                                    label="设备空闲状态"
                                    name="isIdle"
                                    checked={formData.isIdle}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group controlId="formTurnCost" className="mt-3">
                                <Form.Label>每转分数</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.1"
                                    name="turnCost"
                                    value={formData.turnCost}
                                    onChange={handleChange}
                                    isInvalid={!!errors.turnCost}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.turnCost}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    例如 2.8 表示每走转一次，扣 2.8 分
                                </Form.Text>
                            </Form.Group>

                            <Form.Group controlId="formFsFactor" className="mt-3">
                                <Form.Label>下分比例</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.001"
                                    name="fsFactor"
                                    value={formData.fsFactor}
                                    onChange={handleChange}
                                    isInvalid={!!errors.fsFactor}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.fsFactor}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    例如 0.145 表示下分时 rawScore * 0.145
                                </Form.Text>
                            </Form.Group>

                            <Button variant="primary" type="submit" className="mt-4" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        /> 添加中...
                                    </>
                                ) : (
                                    '添加设备'
                                )}
                            </Button>
                        </Form>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default AddDevice;
